import { css, html } from 'lit';
import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { formatMediumDate } from '../../lib/format.js';

class StaffSyncJobsView extends BaseComponent {
    static properties = {
        _jobs: { type: Array, state: true },
        _apps: { type: Array, state: true },
        _loading: { type: Boolean, state: true },
        _error: { type: String, state: true },
        _triggering: { type: Boolean, state: true },
        _selectedAppId: { type: String, state: true },
    };

    static styles = css`
        :host { display: block; }
        .page-header h4 { margin: 0; font-size: 1.5rem; font-weight: 600; }
        .page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem; }
        table { font-size: 0.875rem; }
        .badge-status { font-size: 0.75rem; }
        .progress { height: 6px; }
    `;

    constructor() {
        super();
        this._jobs = [];
        this._apps = [];
        this._loading = true;
        this._error = null;
        this._triggering = false;
        this._selectedAppId = '';
    }

    async connectedCallback() {
        super.connectedCallback();
        this._load();
    }

    async _load() {
        this._loading = true;
        try {
            const [jobsData, appsData] = await Promise.all([
                api.get('/api/sync-jobs'),
                api.get('/api/apps'),
            ]);
            this._jobs = jobsData.jobs || [];
            this._apps = appsData.apps || [];
        } catch (err) {
            this._error = err.message;
        } finally {
            this._loading = false;
        }
    }

    async _triggerSync() {
        this._triggering = true;
        this._error = null;
        try {
            const body = this._selectedAppId ? { app_id: this._selectedAppId } : {};
            await api.post('/api/sync-jobs', body);
            await this._load();
        } catch (err) {
            this._error = err.message;
        } finally {
            this._triggering = false;
        }
    }

    _statusBadge(status) {
        const colors = {
            pending: 'bg-secondary',
            processing: 'bg-primary',
            completed: 'bg-success',
            failed: 'bg-danger',
        };
        return html`<span class="badge ${colors[status] || 'bg-secondary'} badge-status">${status}</span>`;
    }

    _appName(appId) {
        if (!appId) return 'All apps';
        return this._apps.find((a) => a.id === appId)?.name || appId;
    }

    render() {
        return html`
            <div class="page-header">
                <h4>CDN Sync Jobs</h4>
                <div class="d-flex gap-2 align-items-end">
                    <select class="form-select form-select-sm" style="width: 200px;"
                        .value="${this._selectedAppId}"
                        @change="${(e) => (this._selectedAppId = e.target.value)}">
                        <option value="">All apps (full sync)</option>
                        ${this._apps.map((a) => html`<option value="${a.id}">${a.name}</option>`)}
                    </select>
                    <button class="btn btn-primary btn-sm" ?disabled="${this._triggering}" @click="${this._triggerSync}">
                        ${this._triggering ? 'Triggering...' : 'Trigger Sync'}
                    </button>
                    <button class="btn btn-outline-secondary btn-sm" @click="${this._load}">
                        <i class="bi bi-arrow-clockwise"></i>
                    </button>
                </div>
            </div>

            ${this._error ? html`<div class="alert alert-danger">${this._error}</div>` : ''}

            ${
                this._loading
                    ? html`
                <div class="text-center py-4"><div class="spinner-border text-primary"></div></div>
            `
                    : this._jobs.length === 0
                      ? html`
                <p class="text-muted">No sync jobs yet.</p>
            `
                      : html`
                <table class="table table-sm">
                    <thead>
                        <tr>
                            <th>Status</th>
                            <th>Trigger</th>
                            <th>Scope</th>
                            <th>Progress</th>
                            <th>Created</th>
                            <th>Error</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${this._jobs.map(
                            (j) => html`
                            <tr>
                                <td>${this._statusBadge(j.status)}</td>
                                <td>${j.trigger}</td>
                                <td>${this._appName(j.app_id)}</td>
                                <td>
                                    ${
                                        j.total > 0
                                            ? html`
                                        <div class="d-flex align-items-center gap-2">
                                            <div class="progress flex-grow-1">
                                                <div class="progress-bar ${j.failed > 0 ? 'bg-danger' : ''}"
                                                    style="width: ${Math.round((j.processed / j.total) * 100)}%"></div>
                                            </div>
                                            <span class="small text-muted">${j.processed}/${j.total}</span>
                                        </div>
                                    `
                                            : '—'
                                    }
                                </td>
                                <td class="text-muted small">${formatMediumDate(j.created_at)}</td>
                                <td>${j.last_error ? html`<span class="text-danger small">${j.last_error}</span>` : ''}</td>
                            </tr>
                        `,
                        )}
                    </tbody>
                </table>
            `
            }
        `;
    }
}

customElements.define('staff-sync-jobs-view', StaffSyncJobsView);
